import type { DbMeetingStatus } from "@/types/db";

// Shared status semantics for meeting recordings. Both meeting_recordings and
// project_meeting_recordings carry the same `status` enum since migration
// 20260902000001, so personal and project meetings answer these questions the
// same way — there is no second representation to translate to any more.

/** Minutes generation (or the DOCX/email steps that follow) is in flight. */
export const GENERATING_MEETING_STATUSES: ReadonlySet<string> = new Set([
  "PROCESSING_NOTES",
  "PROCESSING_DOCX",
  "SENDING_EMAIL",
]);

/** Audio is still being captured. */
export function isMeetingRecording(status: string | null | undefined): boolean {
  return status === "RECORDING";
}

/** Minutes exist and nothing is currently running. */
export function isMeetingMinutesReady(status: string | null | undefined): boolean {
  return status === "PROCESSING_COMPLETE";
}

/**
 * A human-readable reason the meeting cannot be moved between personal and
 * project scope right now, or null when it is safe to move.
 */
export function meetingBusyLabel(status: string | null | undefined): string | null {
  if (isMeetingRecording(status)) {
    return "Meeting is still recording — wait for it to finish";
  }
  if (status && GENERATING_MEETING_STATUSES.has(status)) {
    return "Minutes are still generating — wait for it to finish";
  }
  return null;
}

/**
 * Columns shared by meeting_recordings and project_meeting_recordings. Used by
 * the attach/detach flows to copy a row wholesale between the two tables
 * without dropping fields. `project_id` and `is_confidential` are excluded —
 * the caller sets those.
 *
 * `id` IS copied: the row keeps its identity across a move, so the DOCX
 * storage path (which embeds the id), token_usage history, and project_items
 * KB rows all stay pointing at the same meeting. A new id would orphan them.
 *
 * `kb_indexed_at` / `kb_indexing_status` are deliberately NOT copied — see
 * KB_RESET_ON_MOVE below.
 */
export const SHARED_MEETING_COLUMNS = [
  "id",
  "user_id",
  "team_id",
  "series_id",
  "title",
  "title_manually_edited",
  "meeting_context",
  "meeting_instructions",
  "transcript",
  "minutes",
  "status",
  "attendees",
  "duration_seconds",
  "transcribed_chunks",
  "failed_chunks",
  "email_sent",
  "email_sent_at",
  "feedback",
  "layout_id",
  "notes_path",
  "previous_meeting_notes",
  "transcript_warning_sent_at",
  "created_at",
] as const;

export type SharedMeetingColumn = (typeof SHARED_MEETING_COLUMNS)[number];

export const SHARED_MEETING_COLUMNS_SELECT = SHARED_MEETING_COLUMNS.join(", ");

/**
 * Knowledge-base index state does NOT survive a move. The index itself is
 * scoped to the destination (project_items rows are per-project, and are
 * deleted on detach), so carrying `kb_indexed_at` across would leave the
 * meeting claiming to be indexed while the destination KB holds nothing for
 * it — and the indexing trigger skips any row that already has kb_indexed_at
 * set, so it would never be indexed at all.
 */
export const KB_RESET_ON_MOVE = {
  kb_indexed_at: null,
  kb_indexing_status: "idle",
} as const;

/**
 * Narrows an arbitrary row to just the shared columns, so a row read from one
 * table can be inserted into the other. `previous_meeting_id` is intentionally
 * NOT copied: it points into whichever table the row came from, and a moved
 * meeting's history does not follow it across the boundary — matching the
 * same-project constraint enforced by the DB triggers.
 */
export function pickSharedMeetingColumns(
  row: Record<string, unknown>
): Record<string, unknown> {
  const picked: Record<string, unknown> = {};
  for (const column of SHARED_MEETING_COLUMNS) {
    if (column in row) picked[column] = row[column];
  }
  return picked;
}

export type MeetingStatus = DbMeetingStatus;
